import Phaser from 'phaser';
import { THEME } from '@/config/theme';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { createModalButton, createInlineButton, type ButtonStyle } from '@/ui/buttons';
import { PLAYER_EDIT_MODAL_LAYOUT } from '@/ui/layout';
import { escapeHtml, formatBinding } from '@/ui/format';
import type { InputBinding } from '@/types/multiplayer';

// ============================================================================
// Modal Configuration Types
// ============================================================================

export interface ModalConfig {
  scene: Phaser.Scene;
  title: string;
  hint?: string;
  width?: number;
  height?: number;
  centerX?: number;
  centerY?: number;
  overlayAlpha?: number;
  overlayDepth?: number;
  panelDepth?: number;
  panelStrokeWidth?: number;
  panelStrokeColor?: number;
  closeOnOverlayClick?: boolean | (() => boolean);
  onDestroy?: () => void;
}

// ============================================================================
// Base Modal Class
// ============================================================================

export abstract class Modal {
  protected scene: Phaser.Scene;
  protected overlay: Phaser.GameObjects.Rectangle;
  protected panel: Phaser.GameObjects.Rectangle;
  protected titleText: Phaser.GameObjects.Text;
  protected hintText?: Phaser.GameObjects.Text;
  protected contentElements: Phaser.GameObjects.GameObject[] = [];
  protected panelBounds?: { contains: (x: number, y: number) => boolean };
  protected closeOnOverlayClick: boolean | (() => boolean);
  protected onDestroyCallback?: () => void;

  /** Get hint text object for updating hint messages during capture */
  public get hint(): Phaser.GameObjects.Text | undefined {
    return this.hintText;
  }

  constructor(config: ModalConfig) {
    this.scene = config.scene;
    this.closeOnOverlayClick = config.closeOnOverlayClick ?? true;
    this.onDestroyCallback = config.onDestroy;

    const centerX = config.centerX ?? GAME_WIDTH / 2;
    const centerY = config.centerY ?? GAME_HEIGHT / 2;
    const width = config.width ?? 760;
    const height = config.height ?? 400;
    const overlayAlpha = config.overlayAlpha ?? 0.65;
    const overlayDepth = config.overlayDepth ?? 200;
    const panelDepth = config.panelDepth ?? 201;
    const panelStrokeWidth = config.panelStrokeWidth ?? 3;
    const panelStrokeColor = config.panelStrokeColor ?? THEME.colors.cyanHex;

    // Create overlay
    this.overlay = this.scene.add.rectangle(centerX, centerY, GAME_WIDTH, GAME_HEIGHT, 0x000000, overlayAlpha);
    this.overlay.setDepth(overlayDepth);
    this.overlay.setInteractive();

    // Create panel
    this.panel = this.scene.add.rectangle(centerX, centerY, width, height, THEME.colors.backgroundPanelHex, 1);
    this.panel.setStrokeStyle(panelStrokeWidth, panelStrokeColor);
    this.panel.setDepth(panelDepth);

    // Store panel bounds for click detection
    this.panelBounds = this.panel.getBounds();

    // Create title
    this.titleText = this.scene.add.text(centerX, centerY - height / 2 + 40, config.title, {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.subheading,
      color: THEME.colors.cyan,
    }).setOrigin(0.5).setDepth(panelDepth + 1);

    // Create hint if provided
    if (config.hint) {
      this.hintText = this.scene.add.text(centerX, centerY - height / 2 + 72, config.hint, {
        fontFamily: THEME.fonts.body,
        fontSize: THEME.sizes.small,
        color: THEME.colors.textMuted,
      }).setOrigin(0.5).setDepth(panelDepth + 1);
    }

    // Setup overlay click handler
    this.overlay.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const closeOnOverlay = typeof this.closeOnOverlayClick === 'function'
        ? this.closeOnOverlayClick()
        : this.closeOnOverlayClick;

      if (closeOnOverlay && this.canCloseOnOverlayClick() && this.panelBounds && !this.panelBounds.contains(pointer.worldX, pointer.worldY)) {
        this.destroy();
      }
    });
  }

  /** Override this in derived classes to control overlay click behavior */
  protected canCloseOnOverlayClick(): boolean {
    return true;
  }

  /** Track an element for automatic cleanup */
  protected trackElement<T extends Phaser.GameObjects.GameObject>(el: T): T {
    this.contentElements.push(el);
    return el;
  }

  /** Clear all tracked content elements */
  protected clearContent(): void {
    this.contentElements.forEach(el => el?.destroy());
    this.contentElements = [];
  }

  /** Update hint text */
  protected setHint(text: string, color?: string): void {
    if (this.hintText) {
      this.hintText.setText(text);
      if (color) this.hintText.setColor(color);
    }
  }

  /** Destroy modal and all tracked elements */
  public destroy(): void {
    this.clearContent();
    this.overlay.destroy();
    this.panel.destroy();
    this.titleText.destroy();
    this.hintText?.destroy();
    this.onDestroyCallback?.();
  }
}

// ============================================================================
// Simple Form Modal (Pattern A)
// ============================================================================

export interface SimpleFormFieldConfig {
  label: string;
  type?: 'text' | 'email';
  placeholder?: string;
  value?: string;
  maxlength?: number;
  y: number;
  width?: number;
}

export interface SimpleFormButtonConfig {
  text: string;
  style: ButtonStyle;
  onClick: (values: Record<string, string>) => void;
  y: number;
  x?: number;
}

export interface SimpleFormConfig extends ModalConfig {
  fields: SimpleFormFieldConfig[];
  buttons: SimpleFormButtonConfig[];
}

export class SimpleFormModal extends Modal {
  private fieldInputs: Phaser.GameObjects.DOMElement[] = [];

  constructor(config: SimpleFormConfig) {
    super({
      ...config,
      height: config.height ?? 320,
    });

    // Build fields
    config.fields.forEach((field) => {
      const centerX = config.centerX ?? GAME_WIDTH / 2;

      // Label
      this.trackElement(this.scene.add.text(centerX - 200, field.y, field.label, {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.body,
        color: THEME.colors.textMuted,
      }).setOrigin(0, 0.5).setDepth(202));

      // Input
      const inputWidth = field.width ?? 400;
      const input = this.scene.add.dom(centerX + 80, field.y).createFromHTML(`
        <input
          type="${field.type ?? 'text'}"
          placeholder="${field.placeholder ?? ''}"
          value="${escapeHtml(field.value ?? '')}"
          maxlength="${field.maxlength ?? 100}"
          style="
            width: ${inputWidth}px;
            height: 46px;
            font-size: 18px;
            font-family: ${THEME.fonts.body};
            padding: 8px 12px;
            border-radius: 8px;
            border: 2px solid ${THEME.colors.cyanHex};
            background: ${THEME.colors.backgroundLight};
            color: ${THEME.colors.text};
            outline: none;
          "
        />
      `);
      input.setOrigin(0.5, 0.5);
      input.setDepth(202);
      this.trackElement(input);
      this.fieldInputs.push(input);
    });

    // Build buttons
    config.buttons.forEach(btn => {
      const btnX = btn.x ?? (config.centerX ?? GAME_WIDTH / 2);
      const button = createModalButton(this.scene, {
        x: btnX,
        y: btn.y,
        text: btn.text,
        style: btn.style,
        onClick: () => {
          const values: Record<string, string> = {};
          config.fields.forEach((field, index) => {
            const input = this.fieldInputs[index]?.node as HTMLInputElement | null;
            values[field.label] = input?.value ?? '';
          });
          btn.onClick(values);
        },
      });
      button.setDepth(202);
      this.trackElement(button);
    });
  }
}

// ============================================================================
// Binding Form Modal (Pattern B)
// ============================================================================

export interface BindingFormConfig extends ModalConfig {
  playerName: string;
  currentBinding: InputBinding;
  onSave: (name: string, binding: InputBinding) => void;
  onCancel: () => void;
  isBindingAllowed?: (binding: InputBinding) => boolean;
  showNameField?: boolean;
}

export class BindingFormModal extends Modal {
  public isCapturing!: boolean;  // Assigned in constructor
  public bindingDraft!: InputBinding;  // Assigned in constructor
  private showNameField!: boolean;  // Assigned in constructor
  private nameInput?: Phaser.GameObjects.DOMElement;
  private bindingValueText?: Phaser.GameObjects.Text;
  private captureButton?: Phaser.GameObjects.Container;
  private ignoreNextPointerDown = false;
  private isBindingAllowed: (binding: InputBinding) => boolean;
  private keyListener?: (event: KeyboardEvent) => void;
  private pointerListener?: (pointer: Phaser.Input.Pointer) => void;

  private saveCallback?: (name: string, binding: InputBinding) => void;

  constructor(config: BindingFormConfig) {
    super({
      ...config,
      title: config.title ?? 'EDIT YOUR SETUP',
      hint: config.hint ?? 'Update name and control, then Save',
      width: PLAYER_EDIT_MODAL_LAYOUT.width,
      height: PLAYER_EDIT_MODAL_LAYOUT.height,
    });

    this.isCapturing = false;
    this.bindingDraft = config.currentBinding;
    this.showNameField = config.showNameField ?? true;

    this.saveCallback = config.onSave;
    this.isBindingAllowed = config.isBindingAllowed ?? (() => true);

    const centerX = config.centerX ?? GAME_WIDTH / 2;
    const centerY = config.centerY ?? GAME_HEIGHT / 2;

    // Name label - only show if enabled
    if (this.showNameField) {
      this.trackElement(this.scene.add.text(centerX - 200, centerY - 60, 'NAME', {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.body,
        color: THEME.colors.textMuted,
      }).setOrigin(0, 0.5).setDepth(202));

      // Name input
      this.nameInput = this.scene.add.dom(centerX + 80, centerY - 60).createFromHTML(`
        <input
          type="text"
          value="${escapeHtml(config.playerName)}"
          maxlength="18"
          style="
            width: ${PLAYER_EDIT_MODAL_LAYOUT.nameInputWidth}px;
            height: ${PLAYER_EDIT_MODAL_LAYOUT.nameInputHeight}px;
            font-size: 18px;
            font-family: ${THEME.fonts.body};
            padding: 8px 12px;
            border-radius: 8px;
            border: 2px solid ${THEME.colors.cyanHex};
            background: ${THEME.colors.backgroundLight};
            color: ${THEME.colors.text};
            outline: none;
          "
        />
      `);
      this.nameInput.setOrigin(0.5, 0.5);
      this.nameInput.setDepth(202);
      this.trackElement(this.nameInput);
    }

    // Binding label - adjust Y position based on whether name field is shown
    const bindingY = this.showNameField ? centerY + 20 : centerY - 30;
    this.trackElement(this.scene.add.text(centerX - 200, bindingY, 'BINDING', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
    }).setOrigin(0, 0.5).setDepth(202));

    // Binding display box
    const bindingBox = this.trackElement(this.scene.add.rectangle(
      centerX + 10, bindingY,
      PLAYER_EDIT_MODAL_LAYOUT.bindingBoxWidth,
      PLAYER_EDIT_MODAL_LAYOUT.bindingBoxHeight,
      THEME.colors.backgroundHex, 0.92
    ));
    bindingBox.setStrokeStyle(2, THEME.colors.cyanHex, 0.7);
    bindingBox.setDepth(202);

    // Binding value text
    this.bindingValueText = this.trackElement(this.scene.add.text(
      centerX + 10, bindingY,
      formatBinding(config.currentBinding), {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.bodyLarge,
        color: THEME.colors.cyan,
      }).setOrigin(0.5).setDepth(202)
    );

    // RE-BIND button
    this.captureButton = createInlineButton(this.scene, {
      x: centerX + 260,
      y: bindingY,
      text: 'RE-BIND',
      width: PLAYER_EDIT_MODAL_LAYOUT.rebindButtonWidth,
      onClick: () => this.beginCapture(),
    });
    this.captureButton.setDepth(202);
    this.trackElement(this.captureButton);

    // Save/Cancel buttons - adjust Y position based on whether name field is shown
    const buttonY = this.showNameField ? centerY + 110 : centerY + 60;

    // Save button
    const saveButton = createModalButton(this.scene, {
      x: centerX - 140,
      y: buttonY,
      text: 'SAVE',
      style: 'primary',
      onClick: () => this.save(),
    });
    saveButton.setDepth(202);
    this.trackElement(saveButton);

    // Cancel button
    const cancelButton = createModalButton(this.scene, {
      x: centerX + 170,
      y: buttonY,
      text: 'CANCEL',
      style: 'secondary',
      onClick: () => {
        this.destroy();
        config.onCancel();
      },
    });
    cancelButton.setDepth(202);
    this.trackElement(cancelButton);

    // Setup event listeners
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // ESC key to cancel capture or close modal
    this.keyListener = (event: KeyboardEvent) => {
      if (this.isCapturing) {
        if (event.key === 'Escape') {
          this.setCapturing(false);
        } else {
          this.handleCaptureInput(event);
        }
      }
    };
    this.scene.input.keyboard?.on('keydown', this.keyListener);

    // Pointer down for mouse capture
    this.pointerListener = (pointer: Phaser.Input.Pointer) => {
      if (this.isCapturing) {
        if (this.ignoreNextPointerDown) {
          this.ignoreNextPointerDown = false;
          return;
        }
        if (pointer.button === 0 || pointer.button === 2) {
          this.handleMouseCapture(pointer);
        }
      }
    };
    this.scene.input.on('pointerdown', this.pointerListener);
  }

  private beginCapture(): void {
    this.ignoreNextPointerDown = true;
    this.setCapturing(true);
  }

  private setCapturing(active: boolean): void {
    this.isCapturing = active;
    if (!active) this.ignoreNextPointerDown = false;

    // Update hint
    this.setHint(active
      ? 'LISTENING... Press a key or click LMB/RMB (Esc to cancel)'
      : (this.showNameField ? 'Update name and control, then Save' : 'Update control, then Save'));

    // Update binding value color
    this.bindingValueText?.setColor(active ? THEME.colors.yellow : THEME.colors.cyan);

    // Update box styles
    if (this.scene.children.list) {
      this.scene.children.list.forEach(child => {
        if (child instanceof Phaser.GameObjects.Rectangle) {
          const bounds = child.getBounds();
          const myBounds = this.panel.getBounds();
          if (bounds.width === PLAYER_EDIT_MODAL_LAYOUT.bindingBoxWidth &&
              bounds.height === PLAYER_EDIT_MODAL_LAYOUT.bindingBoxHeight &&
              Math.abs(bounds.centerX - myBounds.centerX - 10) < 10 &&
              Math.abs(bounds.centerY - myBounds.centerY - 20) < 10) {
            if (active) {
              child.setFillStyle(THEME.colors.magentaHex, 0.18);
              child.setStrokeStyle(2, THEME.colors.yellowHex, 1);
            } else {
              child.setFillStyle(THEME.colors.backgroundHex, 0.92);
              child.setStrokeStyle(2, THEME.colors.cyanHex, 0.7);
            }
          }
        }
      });
    }

    // Update input styling
    const node = this.nameInput?.node as HTMLDivElement | null;
    const input = node?.querySelector('input') as HTMLInputElement | null;
    if (input) {
      input.style.pointerEvents = active ? 'none' : 'auto';
      input.style.opacity = active ? '0.55' : '1';
      if (active) input.blur();
    }

    // Update button
    if (this.captureButton) {
      this.captureButton.iterate((obj: Phaser.GameObjects.GameObject) => {
        if (obj instanceof Phaser.GameObjects.Rectangle) {
          if (active) {
            obj.setFillStyle(THEME.colors.magentaHex, 1);
            obj.setStrokeStyle(2, THEME.colors.yellowHex, 1);
          } else {
            obj.setFillStyle(THEME.colors.backgroundLightHex, 1);
            obj.setStrokeStyle(2, THEME.colors.cyanHex, 1);
          }
        }
        if (obj instanceof Phaser.GameObjects.Text) {
          obj.setText(active ? 'LISTENING' : 'RE-BIND');
        }
      });
    }
  }

  private handleCaptureInput(event: KeyboardEvent): void {
    if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;

    const key = event.key === ' ' ? 'SPACE' : event.key.toUpperCase();
    const candidate: InputBinding = { playerId: this.bindingDraft.playerId, type: 'keyboard', key };

    if (!this.isBindingAllowed(candidate)) {
      this.setHint('That binding is already used. Try another.');
      return;
    }

    this.bindingDraft = candidate;
    this.bindingValueText?.setText(formatBinding(candidate));
    this.setCapturing(false);
  }

  private handleMouseCapture(pointer: Phaser.Input.Pointer): void {
    const candidate: InputBinding = { playerId: this.bindingDraft.playerId, type: 'mouse', mouseButton: pointer.button };

    if (!this.isBindingAllowed(candidate)) {
      this.setHint('That binding is already used. Try another.');
      return;
    }

    this.bindingDraft = candidate;
    this.bindingValueText?.setText(formatBinding(candidate));
    this.setCapturing(false);
  }

  private save(): void {
    let name = '';

    if (this.nameInput) {
      const node = this.nameInput?.node as HTMLDivElement | null;
      const input = node?.querySelector('input') as HTMLInputElement | null;
      name = input?.value ?? '';
    }

    // Destroy modal and invoke callback
    this.destroy();
    this.saveCallback?.(name, this.bindingDraft);
  }

  /** Prevent closing modal while capturing binding */
  protected override canCloseOnOverlayClick(): boolean {
    return !this.isCapturing;
  }

  /** Override close behavior to check capture state */
  public override destroy(): void {
    // Clean up listeners
    if (this.keyListener) this.scene.input.keyboard?.off('keydown', this.keyListener);
    if (this.pointerListener) this.scene.input.off('pointerdown', this.pointerListener);

    super.destroy();
  }
}

// ============================================================================
// Wizard Modal (Pattern C)
// ============================================================================

export interface WizardStageConfig<T extends string = string> {
  name: T;
  render: (modal: WizardModal<T>, error?: string) => void;
}

export interface WizardModalConfig<T extends string = string> extends ModalConfig {
  stages: WizardStageConfig<T>[];
  initialStage: T;
}

export class WizardModal<T extends string = string> extends Modal {
  protected stages: Map<T, WizardStageConfig<T>>;
  public currentStage: T;
  public state: Record<string, unknown> = {};

  constructor(config: WizardModalConfig<T>) {
    super({
      ...config,
      height: config.height ?? 300,
      closeOnOverlayClick: config.closeOnOverlayClick ?? false,
    });

    this.stages = new Map(config.stages.map(s => [s.name, s]));
    this.currentStage = config.initialStage;

    // Only render initial stage if stages were provided
    // (allows derived classes to set up stages after super())
    if (config.stages.length > 0) {
      this.goToStage(config.initialStage);
    }
  }

  /** Go to a stage, optionally with an error message */
  public goToStage(stageName: T, error?: string): void {
    this.currentStage = stageName;
    this.clearContent();

    const stage = this.stages.get(stageName);
    if (stage) {
      stage.render(this, error);
    }
  }

  /** Add a label to the modal content */
  public addLabel(text: string, x: number, y: number, style?: Partial<Phaser.Types.GameObjects.Text.TextStyle>): Phaser.GameObjects.Text {
    const label = this.scene.add.text(x, y, text, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      ...style,
    }).setOrigin(0.5).setDepth(202);
    return this.trackElement(label);
  }

  /** Add an input field to the modal content */
  public addInput(x: number, y: number, options: {
    type?: 'text' | 'email';
    placeholder?: string;
    value?: string;
    maxlength?: number;
    width?: number;
    style?: string;
  }): Phaser.GameObjects.DOMElement {
    const width = options.width ?? 400;
    const inputStyle = options.style ?? `
      width: ${width}px;
      height: 48px;
      font-size: 18px;
      padding: 8px 12px;
      border-radius: 8px;
      border: 2px solid ${THEME.colors.cyanHex};
      background: ${THEME.colors.backgroundLight};
      color: ${THEME.colors.text};
      outline: none;
    `;

    const input = this.scene.add.dom(x, y).createFromHTML(`
      <input
        type="${options.type ?? 'text'}"
        placeholder="${options.placeholder ?? ''}"
        value="${escapeHtml(options.value ?? '')}"
        maxlength="${options.maxlength ?? 100}"
        style="${inputStyle}"
      />
    `);
    input.setOrigin(0.5);
    input.setDepth(2000);  // Match JoinScene pattern
    input.pointerEvents = 'auto';  // Enable pointer events
    input.updateSize();
    // Prevent click from propagating to game input handlers
    input.addListener('click');
    input.on('click', (event: Event) => event.stopPropagation());
    // Ensure HTML element also receives pointer events
    (input.node as HTMLElement | null)?.style?.setProperty('pointer-events', 'auto', 'important');
    return this.trackElement(input);
  }

  /** Add a button to the modal content */
  public addButton(x: number, y: number, options: {
    text: string;
    style: ButtonStyle;
    onClick: () => void;
  }): Phaser.GameObjects.Container {
    const button = createModalButton(this.scene, {
      x,
      y,
      text: options.text,
      style: options.style,
      onClick: options.onClick,
    });
    button.setDepth(202);
    return this.trackElement(button);
  }
}

// ============================================================================
// Save Progress Modal (shared email/code OTP wizard)
// ============================================================================

export interface SaveProgressModalConfig {
  scene: Phaser.Scene;
  onSuccess?: () => void;
  onCancel?: () => void;
  onDestroy?: () => void;
}

export class SaveProgressModal extends WizardModal<'email' | 'code'> {
  private onSuccessCallback?: () => void;

  constructor(config: SaveProgressModalConfig) {
    super({
      scene: config.scene,
      title: 'SAVE PROGRESS',
      hint: 'Link your score with a one-time email code. We never spam.',
      width: 760,
      height: 420,  // Increased from 300 to fit all content
      panelStrokeColor: THEME.colors.magentaHex,
      panelStrokeWidth: 4,
      overlayAlpha: 0.6,
      stages: [],  // Empty - will be set up after super() completes
      initialStage: 'email',
      closeOnOverlayClick: true,  // Allow closing by clicking outside
      onDestroy: () => { config.onDestroy?.(); }
    });

    // Set up callback and stages after super() completes so 'this' is available
    this.onSuccessCallback = config.onSuccess;
    this.stages.set('email', { name: 'email', render: (modal) => this.renderEmailStage(modal) });
    this.stages.set('code', { name: 'code', render: (modal) => this.renderCodeStage(modal) });

    // Render initial stage
    this.goToStage('email');
  }

  private renderEmailStage(modal: WizardModal<'email' | 'code'>): void {
    const centerX = GAME_WIDTH / 2;
    const baseY = GAME_HEIGHT / 2 - 60;

    modal.addLabel('Link your score with a one-time email code. We never spam.', centerX, baseY, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
    });

    const inputY = baseY + 50;
    const emailInput = modal.addInput(centerX, inputY, {
      type: 'email',
      placeholder: 'you@example.com',
      width: 400,
      style: `width: 400px; height: 48px; font-size: 18px; padding: 8px 12px; border-radius: 8px; border: 2px solid ${THEME.colors.cyanHex}; background: ${THEME.colors.backgroundLight}; color: ${THEME.colors.text}; outline: none;`,
    });

    const buttonY = inputY + 85;
    const buttonOffset = 170;

    modal.addButton(centerX + buttonOffset, buttonY, {
      text: 'CANCEL',
      style: 'secondary',
      onClick: () => {
        modal.destroy();
      },
    });

    const message = modal.addLabel('', centerX, inputY + 45, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
    });

    modal.addButton(centerX - buttonOffset, buttonY, {
      text: 'SEND CODE',
      style: 'primary',
      onClick: async () => {
        const inputNode = emailInput.node as HTMLElement | null;
        const el = inputNode?.querySelector('input[type="email"]') as HTMLInputElement | null;
        const email = (el?.value ?? '').trim();

        if (!email) {
          message.setText('Enter your email to send a code.');
          message.setColor(THEME.colors.hit.ok);
          return;
        }

        message.setText('Sending code...');
        message.setColor(THEME.colors.text);

        try {
          await import('@/services/saveProgress').then(m => m.requestEmailOtp(email));
          modal.state['email'] = email;
          modal.state['message'] = '';
          modal.goToStage('code');
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : 'Failed to send code.';
          message.setText(errMsg);
          message.setColor(THEME.colors.hit.ok);
        }
      },
    });
  }

  private renderCodeStage(modal: WizardModal<'email' | 'code'>): void {
    const centerX = GAME_WIDTH / 2;
    const baseY = GAME_HEIGHT / 2 - 60;

    modal.addLabel('Check your email for an 8-digit code.', centerX, baseY, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
    });

    const inputY = baseY + 50;
    const codeInput = modal.addInput(centerX, inputY, {
      placeholder: '8-digit code',
      width: 200,
      style: `width: 200px; height: 48px; font-size: 24px; text-align: center; padding: 8px 12px; border-radius: 8px; border: 2px solid ${THEME.colors.cyanHex}; background: ${THEME.colors.backgroundLight}; color: ${THEME.colors.text}; outline: none; letter-spacing: 4px;`,
    });

    const buttonY = inputY + 85;
    const buttonOffset = 170;

    modal.addButton(centerX + buttonOffset, buttonY, {
      text: 'CANCEL',
      style: 'secondary',
      onClick: () => {
        modal.destroy();
      },
    });

    const message = modal.addLabel('', centerX, inputY + 45, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
    });

    modal.addButton(centerX - buttonOffset, buttonY, {
      text: 'VERIFY',
      style: 'primary',
      onClick: async () => {
        const inputNode = codeInput.node as HTMLElement | null;
        const el = inputNode?.querySelector('input[type="text"]') as HTMLInputElement | null;
        const code = (el?.value ?? '').trim();
        const email = modal.state['email'] as string;

        if (!code) {
          message.setText('Enter the code from your email.');
          message.setColor(THEME.colors.hit.ok);
          return;
        }

        message.setText('Verifying...');
        message.setColor(THEME.colors.text);

        try {
          await import('@/services/saveProgress').then(m => m.verifyEmailOtp(email, code));
          this.onSuccessCallback?.();
          modal.destroy();
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : 'Verification failed.';
          message.setText(errMsg);
          message.setColor(THEME.colors.hit.ok);
        }
      },
    });
  }
}

// ============================================================================
// Confirmation Modal Helper (Pattern D)
// ============================================================================

export interface ConfirmationConfig {
  scene: Phaser.Scene;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  centerX?: number;
  centerY?: number;
  width?: number;
  height?: number;
  overlayAlpha?: number;
  overlayDepth?: number;
  panelDepth?: number;
  panelStrokeWidth?: number;
  panelStrokeColor?: number;
}

export function createConfirmationModal(config: ConfirmationConfig): void {
  const centerX = config.centerX ?? GAME_WIDTH / 2;
  const centerY = config.centerY ?? GAME_HEIGHT / 2;
  const width = config.width ?? 400;
  const height = config.height ?? 180;
  const overlayAlpha = config.overlayAlpha ?? 0.7;
  const overlayDepth = config.overlayDepth ?? 100;
  const panelDepth = config.panelDepth ?? 101;
  const panelStrokeWidth = config.panelStrokeWidth ?? 2;
  const panelStrokeColor = config.panelStrokeColor ?? THEME.colors.magentaHex;

  // Create overlay
  const overlay = config.scene.add.rectangle(centerX, centerY, GAME_WIDTH, GAME_HEIGHT, 0x000000, overlayAlpha);
  overlay.setDepth(overlayDepth);
  overlay.setInteractive();

  // Create panel
  const panel = config.scene.add.rectangle(centerX, centerY, width, height, THEME.colors.backgroundPanelHex, 1);
  panel.setStrokeStyle(panelStrokeWidth, panelStrokeColor);
  panel.setDepth(panelDepth);

  // Create title
  const title = config.scene.add.text(centerX, centerY - 50, config.title, {
    fontFamily: THEME.fonts.heading,
    fontSize: THEME.sizes.heading,
    color: THEME.colors.magenta,
  }).setOrigin(0.5).setDepth(panelDepth + 1);

  // Create message
  const message = config.scene.add.text(centerX, centerY, config.message, {
    fontFamily: THEME.fonts.body,
    fontSize: THEME.sizes.body,
    color: THEME.colors.textMuted,
    wordWrap: { width: width - 40 },
  }).setOrigin(0.5).setDepth(panelDepth + 1);

  // Create buttons using createModalButton for consistent depth and styling
  const confirmBtn = createModalButton(config.scene, {
    x: centerX - 70,
    y: centerY + 50,
    text: config.confirmText ?? 'CONFIRM',
    style: 'primary',
    onClick: () => {
      destroy();
      config.onConfirm();
    },
  });
  confirmBtn.setDepth(202);

  const cancelBtn = createModalButton(config.scene, {
    x: centerX + 70,
    y: centerY + 50,
    text: config.cancelText ?? 'CANCEL',
    style: 'secondary',
    onClick: () => {
      destroy();
      config.onCancel?.();
    },
  });
  cancelBtn.setDepth(202);

  // Overlay click to cancel
  overlay.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
    const bounds = panel.getBounds();
    if (!bounds.contains(pointer.worldX, pointer.worldY)) {
      destroy();
      config.onCancel?.();
    }
  });

  // Cleanup function
  function destroy(): void {
    overlay.destroy();
    panel.destroy();
    title.destroy();
    message.destroy();
    confirmBtn.destroy();
    cancelBtn.destroy();
  }
}
